import {
  ReactNode,
  createContext,
  useState,
  useContext,
  useEffect,
} from "react";
import { Auth, API } from "aws-amplify";
import { useEnv } from "../context/EnvContext";

type UserProviderProps = {
  children: ReactNode;
};

export enum AuthState {
  LOADING = "loading",
  ERROR = "error",
  AUTHORIZING = "authorizing",
  AUTHORIZED = "authorized",
}

type UserContextType = {
  user: any | null;
  alias: string | null;
  location: string | null;
  firstName: string | null;
  jobLevel: string | null;
  authState: AuthState;
};

// Create context with a default value
const UserContext = createContext<UserContextType>({
  user: null,
  alias: null,
  location: null,
  firstName: null,
  jobLevel: null,
  authState: AuthState.LOADING,
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState(null);
  const [alias, setAlias] = useState(null);
  const [location, setLocation] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [jobLevel, setJobLevel] = useState(null);
  const [authState, setAuthState] = useState(AuthState.LOADING);
  const { authConfig } = useEnv();

  useEffect(() => {
    const checkUser = async () => {
      // Check if we're in OAuth callback (has code parameter)
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has('code');
      
      if (hasCode) {
        console.log("OAuth callback detected, waiting for Amplify to process...");
        // Give Amplify time to exchange code for tokens
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      try {
        console.log("Attempting to get authenticated user...");
        const currentUser = await Auth.currentAuthenticatedUser();
        console.log("Current user data:", currentUser);
        console.log("User Session:", currentUser.signInUserSession);
        console.log(
          "ID Token Payload:",
          currentUser.signInUserSession.idToken.payload
        );
        console.log(
          "Username:",
          currentUser.signInUserSession.idToken.payload["custom:USER_NAME"]
        );
        console.log(
          "Location:",
          currentUser.signInUserSession.idToken.payload["custom:LOCATION"]
        );
        console.log(
          "First Name:",
          currentUser.signInUserSession.idToken.payload["custom:FIRSTNAME"]
        );
        setUser(currentUser);
        setAlias(
          currentUser.signInUserSession.idToken.payload["custom:USER_NAME"] ??
            "Unknown"
        );
        setFirstName(
          currentUser.signInUserSession.idToken.payload["custom:FIRSTNAME"]
            ? currentUser.signInUserSession.idToken.payload[
                "custom:FIRSTNAME"
              ].split(" ")[0]
            : "Unknown"
        );
        setJobLevel(
          currentUser.signInUserSession.idToken.payload["custom:JOBLEVEL"]
            ? currentUser.signInUserSession.idToken.payload["custom:JOBLEVEL"]
            : "Unknown"
        );
        setLocation(
          currentUser.signInUserSession.idToken.payload["custom:LOCATION"]
            ? currentUser.signInUserSession.idToken.payload[
                "custom:LOCATION"
              ].substring(0, 4)
            : "Unknown"
        );
        
        // Clear the URL params after successful auth
        if (hasCode) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        configureAPI(currentUser);
        setAuthState(AuthState.AUTHORIZED);
      } catch (error) {
        console.log("Error fetching user:", error);
        
        // Prevent infinite loop: only trigger sign-in if not already in OAuth flow
        const signInAttempted = sessionStorage.getItem('signInAttempted');
        if (hasCode || signInAttempted) {
          console.log("OAuth flow in progress or already attempted, waiting...");
          setAuthState(AuthState.AUTHORIZING);
          // Clear flag and retry after delay
          setTimeout(() => {
            sessionStorage.removeItem('signInAttempted');
            checkUser();
          }, 2000);
          return;
        }
        
        try {
          console.log("🔄 Starting federated sign-in process...");
          sessionStorage.setItem('signInAttempted', 'true');
          setAuthState(AuthState.AUTHORIZING);
          await Auth.federatedSignIn({
            customProvider: "FederateOIDC",
          });
        } catch (signInError) {
          console.log("Error signing in federated user:", signInError);
          sessionStorage.removeItem('signInAttempted');
          setAuthState(AuthState.ERROR);
        }
      }
    };
    checkUser();
  }, [authConfig]);

  const configureAPI = async (user: any) => {
    try {
      if (user) {
        const credentials = await Auth.currentSession();
        API.configure({
          aws_appsync_authentication: credentials,
        });
      }
    } catch (error) {
      console.error("Error configuring API:", error);
    }
  };

  return (
    <UserContext.Provider
      value={{ user, alias, location, authState, firstName, jobLevel }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  console.log("🎣 useUser hook called");
  const context = useContext(UserContext);
  console.log("📊 useUser context value:", context);

  if (context === undefined) {
    console.error("❌ useUser must be used within a UserProvider");
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};
